import { t, Instance, flow } from 'mobx-state-tree';
import type { ReviewQueueItemResponse, ReviewQueueItemType, SuggestionResponse } from 'we-tree-api-client/v2';
import { reviewApi } from '@/api/reviewWrapper';
import { suggestionsApi } from '@/api/suggestionsWrapper';
import { storiesApi } from '@/api/storiesWrapper';
import { mediaApi } from '@/api/mediaWrapper';
import { createSuggestionModelFromResponse } from '@/stores/models/SuggestionModel';

const ReviewQueueItemModel = t.model('ReviewQueueItemModel', {
  id: t.identifier,
  itemType: t.string,
  createdAt: t.string,
  createdBy: t.string,
  title: t.maybeNull(t.string),
  summary: t.maybeNull(t.string),
  targetLabel: t.maybeNull(t.string),
  targetType: t.maybeNull(t.string),
  targetId: t.maybeNull(t.string),
  familyMemberId: t.maybeNull(t.string),
  mimeType: t.maybeNull(t.string),
  originalFilename: t.maybeNull(t.string)
});

const ReviewQueuePreviewItemModel = t.model('ReviewQueuePreviewItemModel', {
  id: t.identifier,
  itemType: t.string,
  title: t.maybeNull(t.string),
  targetLabel: t.maybeNull(t.string),
  createdAt: t.string
});

const ReviewQueueFiltersModel = t.model('ReviewQueueFiltersModel', {
  itemType: t.optional(t.enumeration(['all', 'suggestion', 'story', 'media']), 'all'),
  page: t.optional(t.number, 1),
  pageSize: t.optional(t.number, 20)
});

function toReviewQueueItemModel(item: ReviewQueueItemResponse): Instance<typeof ReviewQueueItemModel> {
  return ReviewQueueItemModel.create({
    id: item.id,
    itemType: item.itemType,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
    title: item.title ?? null,
    summary: item.summary ?? null,
    targetLabel: item.targetLabel ?? null,
    targetType: item.targetType ?? null,
    targetId: item.targetId ?? null,
    familyMemberId: item.familyMemberId ?? null,
    mimeType: item.mimeType ?? null,
    originalFilename: item.originalFilename ?? null
  });
}

export const ReviewQueueStore = t
  .model('ReviewQueueStore', {
    items: t.array(ReviewQueueItemModel),
    total: t.optional(t.number, 0),
    filters: t.optional(ReviewQueueFiltersModel, {}),
    selectedItemId: t.maybeNull(t.string),
    selectedSuggestionDetail: t.maybeNull(t.frozen<SuggestionResponse>()),
    isLoading: t.optional(t.boolean, false),
    isLoadingDetail: t.optional(t.boolean, false),
    isReviewing: t.optional(t.boolean, false),
    error: t.maybeNull(t.string),
    detailError: t.maybeNull(t.string),
    pendingTotal: t.optional(t.number, 0),
    pendingPreview: t.array(ReviewQueuePreviewItemModel),
    isLoadingPendingSummary: t.optional(t.boolean, false),
    pendingSummaryFamilyId: t.maybeNull(t.string)
  })
  .views((self) => ({
    get selectedItem(): Instance<typeof ReviewQueueItemModel> | undefined {
      if (!self.selectedItemId) return undefined;
      return self.items.find((item) => item.id === self.selectedItemId);
    },

    get totalPages(): number {
      return Math.max(1, Math.ceil(self.total / self.filters.pageSize));
    },

    get hasPendingReviews(): boolean {
      return self.pendingTotal > 0;
    }
  }))
  .actions((self) => ({
    setFilterItemType(itemType: Instance<typeof ReviewQueueFiltersModel>['itemType']) {
      self.filters.itemType = itemType;
      self.filters.page = 1;
      self.selectedItemId = null;
      self.selectedSuggestionDetail = null;
    },

    setPage(page: number) {
      self.filters.page = page;
    },

    selectItem(itemId: string | null) {
      if (itemId === self.selectedItemId) return;
      self.selectedItemId = itemId;
      self.selectedSuggestionDetail = null;
      self.detailError = null;
    },

    loadPendingSummary: flow(function* (familyId: string) {
      self.isLoadingPendingSummary = true;

      try {
        const result = yield reviewApi.listReviewQueue(familyId, {
          page: 1,
          pageSize: 5
        });

        if (result.success && result.data) {
          self.pendingSummaryFamilyId = familyId;
          self.pendingTotal = result.data.total ?? 0;
          self.pendingPreview.replace(
            (result.data.items ?? []).slice(0, 5).map((item: ReviewQueueItemResponse) =>
              ReviewQueuePreviewItemModel.create({
                id: item.id,
                itemType: item.itemType,
                title: item.title ?? item.originalFilename ?? null,
                targetLabel: item.targetLabel ?? null,
                createdAt: item.createdAt
              })
            )
          );
        } else if (!(result.error?.isAuthError?.() && result.error?.status === 403)) {
          self.pendingSummaryFamilyId = familyId;
          self.pendingTotal = 0;
          self.pendingPreview.clear();
        }
      } catch {
        self.pendingSummaryFamilyId = familyId;
        self.pendingTotal = 0;
        self.pendingPreview.clear();
      } finally {
        self.isLoadingPendingSummary = false;
      }
    }),

    loadReviewQueue: flow(function* (familyId: string) {
      self.isLoading = true;
      self.error = null;

      try {
        const itemType: ReviewQueueItemType | undefined =
          self.filters.itemType === 'all' ? undefined : (self.filters.itemType as ReviewQueueItemType);

        const result = yield reviewApi.listReviewQueue(familyId, {
          itemType,
          page: self.filters.page,
          pageSize: self.filters.pageSize
        });

        if (result.success && result.data) {
          self.items.replace((result.data.items ?? []).map(toReviewQueueItemModel));
          self.total = result.data.total ?? self.items.length;
          if (self.filters.itemType === 'all' && self.filters.page === 1) {
            self.pendingTotal = self.total;
            self.pendingSummaryFamilyId = familyId;
          }
        } else {
          self.items.clear();
          self.total = 0;
          self.error = result.error?.getUserMessage?.() ?? 'Failed to load review queue';
        }
      } catch (error) {
        self.items.clear();
        self.total = 0;
        self.error = error instanceof Error ? error.message : 'Failed to load review queue';
      } finally {
        self.isLoading = false;
      }
    }),

    loadSuggestionDetail: flow(function* (familyId: string, suggestionId: string) {
      self.isLoadingDetail = true;
      self.detailError = null;

      try {
        const result = yield suggestionsApi.getSuggestion(familyId, suggestionId);
        if (result.success && result.data) {
          self.selectedSuggestionDetail = result.data;
          createSuggestionModelFromResponse(result.data);
        } else {
          self.detailError = result.error?.getUserMessage?.() ?? 'Failed to load suggestion';
        }
      } catch (error) {
        self.detailError = error instanceof Error ? error.message : 'Failed to load suggestion';
      } finally {
        self.isLoadingDetail = false;
      }
    }),

    approveSelected: flow(function* (familyId: string, comment?: string) {
      const item = self.selectedItem;
      if (!item) return { success: false, error: new Error('No item selected') };

      self.isReviewing = true;
      try {
        if (item.itemType === 'suggestion') {
          const result = yield suggestionsApi.approveSuggestion(
            familyId,
            item.id,
            comment?.trim() ? { resolutionComment: comment.trim() } : undefined
          );
          return result;
        }
        if (item.itemType === 'story') {
          return yield storiesApi.approveStory(
            familyId,
            item.id,
            comment?.trim() ? { reviewComment: comment.trim() } : undefined
          );
        }
        if (item.itemType === 'media') {
          return yield mediaApi.approveMedia(familyId, item.id);
        }
        return { success: false, error: new Error('Unknown item type') };
      } finally {
        self.isReviewing = false;
      }
    }),

    rejectSelected: flow(function* (familyId: string, comment?: string) {
      const item = self.selectedItem;
      if (!item) return { success: false, error: new Error('No item selected') };

      self.isReviewing = true;
      try {
        if (item.itemType === 'suggestion') {
          return yield suggestionsApi.rejectSuggestion(
            familyId,
            item.id,
            comment?.trim() ? { resolutionComment: comment.trim() } : undefined
          );
        }
        if (item.itemType === 'story') {
          return yield storiesApi.rejectStory(
            familyId,
            item.id,
            comment?.trim() ? { reviewComment: comment.trim() } : undefined
          );
        }
        if (item.itemType === 'media') {
          return yield mediaApi.rejectMedia(familyId, item.id);
        }
        return { success: false, error: new Error('Unknown item type') };
      } finally {
        self.isReviewing = false;
      }
    }),

    requestChangesOnSelected: flow(function* (familyId: string, comment?: string) {
      const item = self.selectedItem;
      if (!item || item.itemType !== 'suggestion') {
        return { success: false, error: new Error('Request changes only applies to suggestions') };
      }

      self.isReviewing = true;
      try {
        return yield suggestionsApi.requestSuggestionChanges(
          familyId,
          item.id,
          comment?.trim() ? { resolutionComment: comment.trim() } : undefined
        );
      } finally {
        self.isReviewing = false;
      }
    })
  }));

export const reviewQueueStore = ReviewQueueStore.create({});
export type ReviewQueueStoreType = Instance<typeof ReviewQueueStore>;
export type ReviewQueueItemTypeModel = Instance<typeof ReviewQueueItemModel>;
